import React, { useCallback, useState } from "react";
import Cropper from "react-easy-crop";
import GlassPanel from "@/components/GlassPanel";
import { X, ZoomIn, RotateCw, Check } from "lucide-react";

/**
 * Round-crop modal. Takes a dataURL (image), lets the user pan/zoom/rotate,
 * then returns a square JPEG dataURL via onConfirm(dataUrl).
 */
async function getCroppedDataUrl(imageSrc, pixelCrop, rotation = 0, outputSize = 512) {
  const img = await new Promise((res, rej) => {
    const i = new Image();
    i.crossOrigin = "anonymous";
    i.onload = () => res(i);
    i.onerror = rej;
    i.src = imageSrc;
  });
  const canvas = document.createElement("canvas");
  canvas.width = outputSize;
  canvas.height = outputSize;
  const ctx = canvas.getContext("2d");
  const rad = (rotation * Math.PI) / 180;

  // We draw a transformed clipped square based on react-easy-crop's pixelCrop
  const safeArea = Math.max(img.width, img.height) * 2;
  const off = document.createElement("canvas");
  off.width = safeArea;
  off.height = safeArea;
  const octx = off.getContext("2d");
  octx.translate(safeArea / 2, safeArea / 2);
  octx.rotate(rad);
  octx.translate(-safeArea / 2, -safeArea / 2);
  octx.drawImage(img, safeArea / 2 - img.width / 2, safeArea / 2 - img.height / 2);

  const data = octx.getImageData(0, 0, safeArea, safeArea);
  off.width = pixelCrop.width;
  off.height = pixelCrop.height;
  octx.putImageData(
    data,
    Math.round(0 - safeArea / 2 + img.width / 2 - pixelCrop.x),
    Math.round(0 - safeArea / 2 + img.height / 2 - pixelCrop.y),
  );

  ctx.drawImage(off, 0, 0, off.width, off.height, 0, 0, outputSize, outputSize);
  return canvas.toDataURL("image/jpeg", 0.9);
}

export default function AvatarCropper({ open, src, onCancel, onConfirm, busy }) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [pixelCrop, setPixelCrop] = useState(null);

  const onCropComplete = useCallback((_a, area) => setPixelCrop(area), []);

  if (!open) return null;

  const confirm = async () => {
    if (!pixelCrop) return;
    const out = await getCroppedDataUrl(src, pixelCrop, rotation, 512);
    onConfirm(out);
  };

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/85 backdrop-blur-sm grid place-items-center p-4"
      data-testid="avatar-cropper"
      onClick={onCancel}
    >
      <GlassPanel
        className="p-6 w-full max-w-lg relative"
        scanline
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onCancel}
          className="absolute top-4 right-4 text-mute hover:text-white"
          aria-label="Close cropper"
          data-testid="cropper-close"
        >
          <X size={18} />
        </button>
        <div className="gps-tag mb-2">PROFILE PHOTO</div>
        <h2 className="gps-heading text-xl text-white">CROP & FIT</h2>
        <p className="text-mute text-xs mt-1">
          Drag to position. Pinch or use the slider to zoom.
        </p>

        <div className="relative w-full bg-black/60 rounded-xl mt-5 overflow-hidden" style={{ height: 320 }}>
          <Cropper
            image={src}
            crop={crop}
            zoom={zoom}
            rotation={rotation}
            aspect={1}
            cropShape="round"
            showGrid={false}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onRotationChange={setRotation}
            onCropComplete={onCropComplete}
          />
        </div>

        <div className="mt-5 space-y-3">
          <label className="flex items-center gap-3 text-mute text-xs gps-mono tracking-[0.2em]">
            <ZoomIn size={14} /> ZOOM
            <input
              type="range" min={1} max={4} step={0.01}
              value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
              className="flex-1 accent-cyan-400"
              data-testid="cropper-zoom"
            />
          </label>
          <label className="flex items-center gap-3 text-mute text-xs gps-mono tracking-[0.2em]">
            <RotateCw size={14} /> ROTATE
            <input
              type="range" min={0} max={360} step={1}
              value={rotation}
              onChange={(e) => setRotation(Number(e.target.value))}
              className="flex-1 accent-cyan-400"
              data-testid="cropper-rotate"
            />
          </label>
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <button onClick={onCancel} className="gps-btn ghost" data-testid="cropper-cancel">
            CANCEL
          </button>
          <button
            onClick={confirm}
            disabled={busy || !pixelCrop}
            className="gps-btn"
            data-testid="cropper-confirm"
          >
            <Check size={14} /> {busy ? "UPLOADING..." : "APPLY"}
          </button>
        </div>
      </GlassPanel>
    </div>
  );
}
